import {BreadcrumbAdmin} from '@/components/breadcrumb/BreadcrumbAdmin.tsx'
import {SidebarAdmin} from '@/components/sidebar/SidebarAdmin.tsx'
import {menuTabs} from '@/components/sidebar/sidebarTabsAdmin.tsx'
import {AccountFormAdmin} from '@/pages/account/AccountFormAdmin.tsx'
import {AccountListAdmin} from '@/pages/account/AccountListAdmin.tsx'
import AgencyForm from '@/pages/agency/AgencyForm.tsx'
import AgencyList from '@/pages/agency/AgencyList.tsx'
import {AuthLogListAdmin} from '@/pages/auth-log/AuthLogListAdmin.tsx'
import {FilterListGlobalFormAdmin} from '@/pages/filter-list-global/FilterListGlobalFormAdmin.tsx'
import {FilterListGlobalListAdmin} from '@/pages/filter-list-global/FilterListGlobalListAdmin.tsx'
import {FilterListFormAdmin} from '@/pages/filter-list/FilterListFormAdmin.tsx'
import {FilterListListAdmin} from '@/pages/filter-list/FilterListListAdmin.tsx'
import {LogListAdmin} from '@/pages/log/LogListAdmin.tsx'
import MandatoryFieldList from '@/pages/mandatory-field/MandatoryFieldList.tsx'
import {ReportFormAdmin} from '@/pages/report/ReportFormAdmin.tsx'
import {SavedReportListAdmin} from '@/pages/report/SavedReportListAdmin.tsx'
import SettingList from '@/pages/settings/SettingList.tsx'
import {SspFormAdmin} from '@/pages/ssp/SspFormAdmin.tsx'
import {SspListAdmin} from '@/pages/ssp/SspListAdmin.tsx'
import {TrackerEventStatsAdmin} from '@/pages/tracker-event-stats/TrackerEventStatsAdmin.tsx'
import AgencyTransactionForm from '@/pages/transactions/AgencyTransactionForm.tsx'
import {Layout} from 'antd'
import {ReportRequestParametersObjectObject} from 'common/api/api.ts'
import {ErrorModal} from 'common/components/ErrorModal.tsx'
import {CommonRoutes, normalizeRoutePath, normalizeSelectedMenuKey} from 'common/context/commonRoutes.ts'
import {FormMode, isAuthenticated} from 'common/util/Util.tsx'
import * as React from 'react'
import {useState} from 'react'
import {Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom'
import {AgencyTransactionListAdmin} from '@/pages/transactions/AgencyTransactionListAdmin.tsx'
import TrackerEventList from "@/pages/tracker-event/TrackerEventList.tsx";
import TrackerEventForm from "@/pages/tracker-event/TrackerEventForm.tsx";

const { Content, Header } = Layout

export const AppAdmin = () => {
  const [openMenuKeys, setOpenMenuKeys] = React.useState<string[]>([])
  const [selectedMenuKeys, setSelectedMenuKeys] = React.useState<string[]>([])
  const [collapsed, setCollapsed] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()

  React.useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login')
      return
    }

    const updateSelectedKeys = () => {
      const path = location.pathname
      const parentKey = getParentMenuKey(path)
      const normalizedSelectedKey = normalizeSelectedMenuKey(path)

      if (parentKey) {
        setOpenMenuKeys((prev) => (prev.includes(parentKey) ? prev : [...prev, parentKey]))
      }
      setSelectedMenuKeys([normalizedSelectedKey])
    }

    updateSelectedKeys()
  }, [location])

  const getParentMenuKey = (pathName: string) => {
    const selectedKey = normalizeSelectedMenuKey(pathName)
    const found = menuTabs.find((item) =>
      item.children?.some((child) => normalizeRoutePath(child.url) === selectedKey))
    return found ? found.key : ''
  }

  const handleMenuClick = (param: any) => setSelectedMenuKeys([param.key])
  const handleOpenChange = (keys: string[]) => setOpenMenuKeys(keys)
  const reportMenuSelected = (openKey: string, selectedKey: string) => {
    setOpenMenuKeys((prev) => (prev.includes(openKey) ? prev : [...prev, openKey]))
    setSelectedMenuKeys([selectedKey])
  }

  if (!isAuthenticated()) return <Navigate to="/login" />

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <SidebarAdmin
        openKeys={openMenuKeys}
        selectedKeys={selectedMenuKeys}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        onOpenChange={handleOpenChange}
        handleClick={handleMenuClick}
      />
      <Layout>
        <Header style={{ padding: 0, backgroundColor: '#0D1A43', display: 'flex', alignItems: 'center', height: 60 }}>
          <BreadcrumbAdmin collapsed={collapsed} />
        </Header>
        <ErrorModal />
        <Content style={{ margin: 16 }}>
          <Routes>
            <Route path="/" element={<Navigate to="/agency" replace />} />

            <Route path="/agency" element={<AgencyList menuSelected={reportMenuSelected} />} />
            <Route path="/agency/create" element={<AgencyForm mode={FormMode.create} />} />
            <Route path="/agency/:id/edit" element={<AgencyForm mode={FormMode.edit} />} />

            <Route path="/account" element={<AccountListAdmin />} />
            <Route path="/account/create" element={<AccountFormAdmin mode={FormMode.create} />} />
            <Route path="/account/:id/edit" element={<AccountFormAdmin mode={FormMode.edit} />} />

            <Route
              path={CommonRoutes.REPORT}
              element={<SavedReportListAdmin reportType={ReportRequestParametersObjectObject.TypeEnum.Stats} />}
            />
            <Route path={CommonRoutes.REPORT_BUILDER} element={<ReportFormAdmin mode={FormMode.create} />} />
            <Route path={CommonRoutes.REPORT_COPY} element={<ReportFormAdmin mode={FormMode.copy} />} />
            <Route path={CommonRoutes.REPORT_EDIT} element={<ReportFormAdmin mode={FormMode.edit} />} />
            <Route path={CommonRoutes.REPORT_OPEN} element={<ReportFormAdmin mode={FormMode.edit} autoLoad={true} />} />

            <Route path="/ssp" element={<SspListAdmin />} />
            <Route path="/ssp/create" element={<SspFormAdmin mode={FormMode.create} />} />
            <Route path="/ssp/copy/:id" element={<SspFormAdmin mode={FormMode.copy} />} />
            <Route path="/ssp/:id/edit" element={<SspFormAdmin mode={FormMode.edit} />} />

            <Route path={CommonRoutes.FILTER_LIST} element={<FilterListListAdmin />} />
            <Route path={CommonRoutes.FILTER_LIST_CREATE} element={<FilterListFormAdmin mode={FormMode.create} />} />
            <Route path={CommonRoutes.FILTER_LIST_EDIT} element={<FilterListFormAdmin mode={FormMode.edit} />} />

            <Route path={CommonRoutes.FILTER_LIST_GLOBAL} element={<FilterListGlobalListAdmin />} />
            <Route
              path={CommonRoutes.FILTER_LIST_GLOBAL_CREATE}
              element={<FilterListGlobalFormAdmin mode={FormMode.create} />}
            />
            <Route
              path={CommonRoutes.FILTER_LIST_GLOBAL_EDIT}
              element={<FilterListGlobalFormAdmin mode={FormMode.edit} />}
            />

            <Route
              path={CommonRoutes.TRACKER_EVENT_REPORT}
              element={
                <SavedReportListAdmin
                  reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker}
                  createRoute={CommonRoutes.TRACKER_EVENT_REPORT_CREATE}
                  reportRouteBase={CommonRoutes.TRACKER_EVENT_REPORT}
                  title="Tracker Reports"
                />
              }
            />
            <Route path={CommonRoutes.TRACKER_EVENT_REPORT_CREATE} element={<TrackerEventStatsAdmin />} />
            <Route
              path={CommonRoutes.TRACKER_EVENT_REPORT_EDIT}
              element={
                <ReportFormAdmin
                  mode={FormMode.edit}
                  reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker}
                  savedReportBaseRoute={CommonRoutes.TRACKER_EVENT_REPORT}
                />
              }
            />
            <Route
              path={CommonRoutes.TRACKER_EVENT_REPORT_COPY}
              element={
                <ReportFormAdmin
                  mode={FormMode.copy}
                  reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker}
                  savedReportBaseRoute={CommonRoutes.TRACKER_EVENT_REPORT}
                />
              }
            />

            <Route path={CommonRoutes.TRACKER_EVENT} element={<TrackerEventList />} />
            <Route path={CommonRoutes.TRACKER_EVENT_CREATE} element={<TrackerEventForm mode={FormMode.create} />} />
            <Route path={CommonRoutes.TRACKER_EVENT_EDIT} element={<TrackerEventForm mode={FormMode.edit} />} />

            <Route path="/agency-transactions" element={<AgencyTransactionListAdmin />} />
            <Route path="/agency-transactions/create/:id?" element={<AgencyTransactionForm />} />

            <Route path="/logs" element={<LogListAdmin />} />
            <Route path="/logs/:entity/:id" element={<LogListAdmin />} />
            <Route path="/logs/:entity" element={<LogListAdmin />} />

            <Route path="/auth-logs" element={<AuthLogListAdmin />} />

            <Route path="/mandatory-fields" element={<MandatoryFieldList />} />

            <Route path="/settings" element={<SettingList />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  )
}
